/**
 * The rows of the project group settings page.
 *
 * Split from `ProjectGroupSettings`, which passed its 400-line cap once the
 * Icon, Workspace and Danger rows landed. The page is now a composition — which
 * sections exist, in what order — and each row keeps its own copy decisions
 * next to the control they govern.
 *
 * Every row here shares one rule: a control that cannot save must not be
 * offered. Taking a choice and discarding it in silence is the failure these
 * rows were written to avoid, so the writable ones disable and the read-only
 * ones render no control at all.
 */

import { Trash2Icon } from "lucide-react";

import type { EnvironmentId, ThreadEnvMode } from "@cmmd-forge/contracts";

import { useSettings } from "~/hooks/useSettings";
import { getCustomModelOptionsByProvider } from "~/modelSelection";
import { useServerProviders } from "~/rpc/serverState";
import type { CheckoutOption, CheckoutRemoval, GroupWritability } from "~/projectCheckoutOptions";
import type { ProjectDefaultModelPair, ProjectDefaultModelState } from "~/projectDefaultModel";
import type { ProjectGroupDeletionPlan } from "~/projectGroupDeletion";
import type { ProjectGroupingMode } from "~/projectGrouping";
export type { ProjectThreadEnvModeState } from "~/projectThreadEnvMode";
import type { ProjectThreadEnvModeState } from "~/projectThreadEnvMode";
import { resolveProjectIconProvenance } from "~/projectIconProvenance";
import { ProjectIcon, type ProjectCanonicalIdentity } from "../ProjectIcon";
import { ProviderModelPicker } from "../chat/ProviderModelPicker";
import { PROVIDER_AVAILABILITY_PAYLOAD_ONLY } from "../chat/modelPickerProviderAvailability";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { SettingResetButton, SettingsRow, SettingsSection } from "../settings/settingsLayout";

/**
 * Drop one checkout out of the group, leaving the others alone.
 *
 * Offered only when the group has another checkout to fall back to: removing
 * the last one deletes the group's final project row, so this page stops
 * resolving and anything set on it has nothing left to be stored against.
 * Removing a whole project is the sidebar's job.
 *
 * The description is not fixed copy. A local checkout keeps its working copy
 * on disk; a cloud one IS its working copy, so the same action destroys a
 * runtime and its volume. Promising "files are not touched" for both would be
 * a lie told at the one moment it costs someone their work.
 */
export function RemoveCheckoutRow(props: {
  readonly removal: CheckoutRemoval;
  readonly onRemove: () => Promise<void>;
}) {
  if (props.removal.kind !== "removable") return null;

  return (
    <SettingsRow
      title="Remove checkout"
      // One sentence, not two. The old copy branched on whether the checkout was
      // cloud and promised to destroy its runtime and volume — true while a
      // Sprite backed exactly one Project, and a lie about a machine other
      // Projects run on now. Ending the Environment is Settings → Connections.
      // t3code's wording verbatim.
      description="Removes this checkout and its threads from the project group. Files on disk are not touched."
      control={
        <Button size="xs" variant="destructive-outline" onClick={() => void props.onRemove()}>
          <Trash2Icon className="size-3.5" />
          Remove checkout
        </Button>
      }
    />
  );
}

/**
 * The model new threads in this project start on, shared by the whole group.
 *
 * Group-scoped rather than per-checkout because otherwise the same project
 * would start threads on different models depending on which machine you
 * opened it from. The reset appears only once a person has actually chosen
 * something — offering to undo an inherited default promises an undo for a
 * decision nobody made.
 */
export function DefaultModelRow(props: {
  readonly defaultModel: ProjectDefaultModelState;
  readonly writability: GroupWritability;
  readonly onChange: (selection: ProjectDefaultModelPair | null) => void;
}) {
  const settings = useSettings();
  const providers = useServerProviders();
  const { provider, model } = props.defaultModel.selection;

  return (
    <SettingsRow
      title="Model"
      description="New threads in this project start with this model. Applies to every checkout."
      resetAction={
        props.defaultModel.isExplicit && props.writability.kind === "writable" ? (
          <SettingResetButton label="project default model" onClick={() => props.onChange(null)} />
        ) : null
      }
      control={
        <ProviderModelPicker
          provider={provider}
          model={model}
          // A picker that cannot save is worse than none: it takes the choice,
          // plans zero updates against zero writable members, and discards it
          // without saying so. Same reason the Name field refuses.
          disabled={props.writability.kind !== "writable"}
          lockedProvider={null}
          providers={providers}
          providerAvailability={PROVIDER_AVAILABILITY_PAYLOAD_ONLY}
          modelOptionsByProvider={getCustomModelOptionsByProvider(
            settings,
            providers,
            provider,
            model,
          )}
          triggerVariant="outline"
          triggerClassName="min-w-0 max-w-none shrink-0 text-foreground/90 hover:text-foreground"
          onProviderModelChange={(nextProvider, nextModel) =>
            props.onChange({ provider: nextProvider, model: nextModel })
          }
        />
      }
    />
  );
}

/**
 * Where this project's new threads start, shared by the whole group.
 *
 * NOT titled "Workspace": `CONTEXT.md` lists that word under _Avoid_ for
 * Environment, and the command palette already labels project groups
 * "Workspaces" meaning CMMD workspaces. Two meanings for one word in one
 * product is one too many, so the row is named after what it does.
 *
 * The app-wide setting cannot be right for two projects at once: a repo whose
 * work belongs in worktrees and a scratch checkout you always edit in place are
 * both normal, and under one global value one of them always loses. This pins
 * the answer per project.
 *
 * A pin, not a lock — picking a mode for a single thread still wins, which is
 * why the resolver puts an explicit request above this. The copy matches the
 * global Threads row exactly, because a reader who has met one of these rows
 * should not have to learn a second vocabulary for the same choice.
 */
export function ThreadWorkspaceRow(props: {
  readonly threadEnvMode: ProjectThreadEnvModeState;
  readonly writability: GroupWritability;
  readonly onChange: (mode: ThreadEnvMode | null) => void;
}) {
  const canWrite = props.writability.kind === "writable";

  return (
    <SettingsRow
      title="New threads start in"
      description="Applies to every checkout of this repository."
      resetAction={
        props.threadEnvMode.isExplicit && canWrite ? (
          <SettingResetButton label="project workspace" onClick={() => props.onChange(null)} />
        ) : null
      }
      control={
        <Select
          value={props.threadEnvMode.mode}
          onValueChange={(value) => {
            if (value === "local" || value === "worktree") props.onChange(value);
          }}
        >
          <SelectTrigger
            aria-label="New threads start in"
            className="w-full sm:w-44"
            // Same reason as the Model row: a control that cannot save takes
            // the choice, plans zero updates against zero writable members, and
            // discards it without saying so.
            disabled={!canWrite}
          >
            <SelectValue>
              {props.threadEnvMode.mode === "worktree" ? "New worktree" : "Project Folder"}
            </SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            <SelectItem hideIndicator value="local">
              Project Folder
            </SelectItem>
            <SelectItem hideIndicator value="worktree">
              New worktree
            </SelectItem>
          </SelectPopup>
        </Select>
      }
    />
  );
}

/**
 * The project's icon, and which system owns it.
 *
 * Reports provenance rather than taking an edit. A CMMD-backed project already
 * owns its identity, and a Forge-side icon field would be a second copy of it —
 * the mirror this repo deleted once already, which looked authoritative exactly
 * when it was stale. A plain checkout's icon is a favicon in its own repository,
 * so that is where its owner is sent.
 *
 * No control, deliberately. Rendering a picker that cannot save would take a
 * choice and discard it, which is the failure the Model and Workspace rows
 * above guard against.
 */
export function ProjectIconRow(props: {
  readonly canonicalIdentity: ProjectCanonicalIdentity | undefined;
  readonly name: string;
  readonly checkout: CheckoutOption | null;
}) {
  const provenance = resolveProjectIconProvenance(props.canonicalIdentity);

  return (
    <SettingsRow
      title="Icon"
      description={provenance.description}
      control={
        <ProjectIcon
          environmentId={(props.checkout?.environmentId ?? "") as EnvironmentId}
          cwd={props.checkout?.workspaceRoot ?? ""}
          projectName={props.name}
          canonicalIdentity={props.canonicalIdentity}
          className="size-6"
        />
      }
    />
  );
}

/**
 * Why the Name row is or is not editable, in the reader's terms.
 *
 * The three cases need three sentences. "No checkout here" and "the checkout is
 * a sprite nothing on this machine is connected to" are different situations,
 * and telling a reader on forge-cloud to "open this repository from a
 * connection" when they are standing on one is the collapse this avoids.
 */
export function describeNameRow(writability: GroupWritability): string {
  if (writability.kind === "writable") return "Shared by every checkout of this repository.";
  if (writability.kind === "no-checkout") {
    return "Open this repository from a connection to name it.";
  }
  return `Connect to ${writability.label} to name this project.`;
}

/**
 * Delete the whole project — every checkout of it.
 *
 * A different action from "Remove checkout" above, and the copy has to make
 * that obvious: one drops a single working copy out of the group, this ends
 * the project everywhere. The checkout count is the safety feature, because
 * "delete forge" reads very differently at one checkout than at twenty, and
 * twenty is a real number for this repo.
 *
 * The blast-radius sentence branches on whether the group contains a sprite. A
 * local checkout keeps its files on disk; a cloud one IS its working copy, so
 * the same action destroys a runtime and its volume. Inheriting the local copy
 * would promise "files are not touched" at the one moment that promise costs
 * someone their work.
 *
 * Hidden entirely for a group with nothing in it: `createRemovableForgeProject`
 * throws on an empty target list, so a button here would throw rather than
 * explain.
 */
export function DangerZoneSection(props: {
  readonly deletion: ProjectGroupDeletionPlan;
  readonly onDelete: () => Promise<void>;
}) {
  if (!props.deletion.deletable) return null;

  const { checkoutCount } = props.deletion.summary;
  // "Remove", not "Delete": the entries go and the files stay. Two branches,
  // t3code's wording verbatim — and neither mentions a machine, because neither
  // action touches one.
  const spansMachines = checkoutCount > 1;

  return (
    <SettingsSection title="Danger zone">
      <SettingsRow
        title={spansMachines ? "Remove this project everywhere" : "Remove project"}
        description={
          spansMachines
            ? `Deletes all ${String(checkoutCount)} checkout entries and their threads on every machine. Files on disk are not touched.`
            : "Deletes the project entry and its threads. Files on disk are not touched."
        }
        control={
          <Button size="xs" variant="destructive-outline" onClick={() => void props.onDelete()}>
            <Trash2Icon className="size-3.5" />
            {spansMachines ? "Remove all entries" : "Remove project"}
          </Button>
        }
      />
    </SettingsSection>
  );
}

/** The grouping choice for this project's repository, and whether it applies. */
export interface ProjectGroupingRowState {
  readonly mode: ProjectGroupingMode;
  /**
   * False for a project with no repository identity: there is nothing to key
   * the choice on and no in-repo subpath to collapse, so the control would
   * change nothing.
   */
  readonly available: boolean;
}

/**
 * Whether this repository's packages are one project or several.
 *
 * The only grouping choice this codebase actually implies. The logical key
 * carries the in-repo subpath, so `apps/web` and `apps/server` of one
 * repository are separate projects by default — right for a monorepo whose
 * packages are different work, wrong for one you think of as a single thing.
 *
 * Per repository rather than per group, and per device rather than fanned out.
 * A per-group choice would be circular, since grouping is what decides which
 * projects form a group; and how someone arranges their own sidebar is not a
 * property of the project, so writing it to every checkout would rearrange
 * other people's.
 *
 * Hidden when it would not apply, rather than shown and inert — the same rule
 * every other row on this page follows.
 */
export function ProjectGroupingRow(props: {
  readonly grouping: ProjectGroupingRowState;
  readonly onChange: (mode: ProjectGroupingMode) => void;
}) {
  if (!props.grouping.available) return null;

  return (
    <SettingsRow
      title="Grouping"
      description="Whether this repository's packages appear as one project or several. Applies on this device."
      control={
        <Select
          value={props.grouping.mode}
          onValueChange={(value) => {
            if (value === "package" || value === "repository") props.onChange(value);
          }}
        >
          <SelectTrigger aria-label="Project grouping" className="w-full sm:w-44">
            <SelectValue>
              {props.grouping.mode === "repository" ? "Repository" : "Package folder"}
            </SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            <SelectItem hideIndicator value="package">
              Package folder
            </SelectItem>
            <SelectItem hideIndicator value="repository">
              Repository
            </SelectItem>
          </SelectPopup>
        </Select>
      }
    />
  );
}
